// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';

import LoginPage      from './LoginPage';
import AdminDashboard   from './AdminDashboard';
import ManagerDashboard from './ManagerDashboard';
import PartnerDashboard from './PartnerDashboard';
import RequestAccessPage from './RequestAccessPage';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useIdleLogout } from './useIdleLogout';

const theme = createTheme();

const getHomeRouteForProfile = (profile) => {
    switch (profile?.tipoUsuario) {
        case 'administrador': return '/admin';
        case 'gerente':       return '/manager';
        default:              return '/portal';
    }
};

function App() {
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading]         = useState(true);

    // Desloga automaticamente após 1 hora de inatividade
    useIdleLogout(60 * 60 * 1000);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                const docRef  = doc(db, 'usuarios', currentUser.uid);
                const docSnap = await getDoc(docRef);
                setUserProfile(docSnap.exists() ? { uid: currentUser.uid, ...docSnap.data() } : null);
            } else {
                setUserProfile(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Chamado pelo ChangePasswordDialog (modo forçado) após troca bem-sucedida
    const handleFirstAccessComplete = () => {
        setUserProfile(prev => prev ? { ...prev, primeiroAcesso: false } : prev);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const PrivateRoute = ({ children, allowedRoles }) => {
        if (!userProfile) return <Navigate to="/login" />;
        // Bloqueia usuários inativos mesmo com token Firebase válido
        if (userProfile.status && userProfile.status !== 'ativo') return <Navigate to="/login" />;
        if (!allowedRoles.includes(userProfile.tipoUsuario)) {
            return <Navigate to={getHomeRouteForProfile(userProfile)} />;
        }
        return children;
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <Routes>
                    {/* Rotas Públicas */}
                    <Route
                        path="/login"
                        element={!userProfile ? <LoginPage /> : <Navigate to={getHomeRouteForProfile(userProfile)} />}
                    />
                    <Route path="/solicitar-cadastro" element={<RequestAccessPage />} />

                    {/* Rotas Privadas */}
                    <Route
                        path="/admin/*"
                        element={
                            <PrivateRoute allowedRoles={['administrador']}>
                                <AdminDashboard
                                    userProfile={userProfile}
                                    onFirstAccessComplete={handleFirstAccessComplete}
                                />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/manager/*"
                        element={
                            <PrivateRoute allowedRoles={['gerente']}>
                                <ManagerDashboard
                                    userProfile={userProfile}
                                    onFirstAccessComplete={handleFirstAccessComplete}
                                />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/portal"
                        element={
                            <PrivateRoute allowedRoles={['autonomo', 'imobiliaria']}>
                                <PartnerDashboard
                                    userProfile={userProfile}
                                    onFirstAccessComplete={handleFirstAccessComplete}
                                />
                            </PrivateRoute>
                        }
                    />

                    <Route path="*" element={<Navigate to="/login" />} />
                </Routes>
            </Router>
        </ThemeProvider>
    );
}

export default App;
